import { inject } from '@angular/core';
import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import {
    catchError, concatMap, debounceTime, filter, forkJoin, from, map, merge, mergeMap, of, switchMap,
    take, takeUntil, takeWhile, timer,
} from 'rxjs';

import { GetBlueSageTerminalPushStatusGQL, TransactionStage } from 'src/generated/graphql.generated';

import { JobToolActions } from '../job/job-tool.actions';
import { ActionSource } from '../state/action-sources';

import { CancelPreauthDialogActions } from '../transactions/cancel-preauth-dialog/cancel-preauth-dialog.actions';
import { CreateTransactionDialogActions } from '../transactions/create-transaction-dialog/create-transaction-dialog.actions';
import { selectTransactionJob } from '../transactions/transaction-job.selectors';

import { BlueSageRefundLegActions } from './bluesage-refund-leg.actions';
import { blueSageRefundLegOfAttempt, describeBlueSageRefundFailure } from './bluesage.util';
import { BlueSageRefundDialogActions } from './refund-dialog/bluesage-refund-dialog.actions';
import { BlueSageTerminalPushActions } from './terminal-push/bluesage-terminal-push.actions';
import { blueSageTerminalPushFeature } from './terminal-push/bluesage-terminal-push.feature';

/**
 * Keep the job's transaction rows honest while BlueSage settles.
 *
 * ## The problem this exists for
 *
 * **Settlement is asynchronous and slow, and the job page never refreshed
 * itself.** A charge returns approved in seconds, but the row only becomes
 * `paid` when `transaction.approved` arrives and `markPaid` applies it — and
 * measured against production on 2026-08-28 that event's own `occurred_at` ran
 * **34 to 218 seconds** behind the transaction being created. A refund leg is
 * worse: `refundTransaction` answers `Boolean!`, so the new row does not exist
 * client-side at all until the job is read again.
 *
 * With nothing polling, the operator's only tool was the browser reload — and
 * because a reload a minute in is still too early, the honest report from
 * testing was *"180 seconds went by and the refund didn't show up… after
 * refund it still didn't show… refund now showed up after another 120
 * seconds"*. The money was always correct. The screen was not.
 *
 * ## Why a poll and not a subscription
 *
 * The settling fact arrives as a webhook into the BACKEND. Nothing pushes it to
 * this client, and adding a GraphQL subscription for it is a much larger change
 * to vendor infrastructure than the problem warrants. Re-reading the job is the
 * mechanism the page already has (a `network-only` re-read), so this reuses it
 * rather than inventing a second path to the same data.
 *
 * ⚠ It goes through `backgroundRefreshTriggered`, NOT `retryFetchJobTriggered`.
 * A failed `retryFetchJobTriggered` emits `fetchJobError`, which
 * `redirectToJobPage` turns into navigating away from the job — right when
 * someone opened a job that cannot be loaded, and badly wrong when a background
 * poll blipped while an operator has a customer at the terminal. Ejecting them
 * off the job is far worse than the stale row this exists to fix.
 *
 * ## Why it stops
 *
 * Two ways, and both matter. It stops the moment **anything about the job's
 * transactions changes** — a new row, a stage change, a deletion — because that
 * is the event being waited for and a poll that continued would be pure waste
 * on a query that is not cheap. And it stops when the schedule runs out, so a
 * job holding a permanently pending row (an open authorization, an unpaid pay
 * link) cannot leave a timer running forever.
 *
 * The schedule backs off deliberately: most settle inside 90 seconds, and the
 * cost of asking is a full job read.
 */

/**
 * The action the job's own `jobChanged` subscription dispatches when a
 * transaction is updated anywhere — including by the webhook handler that
 * settles it.
 *
 * `transactions.update` already publishes this, and the comment beside it says
 * why: *"Reusing `emitJobChanged` means the job screen already listens, so this
 * is the smallest change that makes the 'paid' flip live."* The publish works.
 * What was missing is a listener: the subscription dispatches the action with a
 * **dynamically built type**, and the `JobToolSubscriptionActions` group only
 * declares `job updated` and the comment events — so `transaction updated`
 * arrived, matched no reducer and no effect, and changed nothing on screen.
 *
 * Matched by string for that reason. It is not a typed action anywhere, and
 * inventing a typed one here would not make the dispatched action match it.
 */
const TRANSACTION_UPDATED_ACTIONS = [
    `[${ ActionSource.JOB_TOOL_SUBSCRIPTION }] remote transaction updated`,
    `[${ ActionSource.JOB_TOOL_SUBSCRIPTION }] local transaction updated`,
];

/** Long enough to coalesce one settlement's updates, short enough to feel live. */
const TRANSACTION_UPDATE_DEBOUNCE_MS = 750;

/** Backing off, in ms from the trigger. Ten reads over ~2.5 minutes. */
const SETTLEMENT_POLL_DELAYS_MS = [
    4_000, 4_000, 6_000, 8_000, 12_000, 16_000, 20_000, 25_000, 30_000, 30_000,
];

/**
 * What "nothing has changed yet" means, as one comparable string.
 *
 * Ids, stages and deletions — the three things a settlement, a refund leg or a
 * discarded push can alter. Amounts are deliberately absent: they do not change
 * under us, and including them would make an unrelated edit stop the poll.
 */
function transactionsFingerprint(job: { transactions?: {
    id: string;
    stage?: string | null;
    deletedAt?: number | null;
}[] } | undefined): string {
    return (job?.transactions ?? [])
        .map((t) => `${ t.id }:${ t.stage ?? '' }:${ t.deletedAt ?? '' }`)
        .sort()
        .join('|');
}

export const refreshJobWhileBlueSageSettles = createEffect((
    actions$ = inject(Actions),
    store = inject(Store),
) => {
    return actions$.pipe(
        ofType(
            // Money moved, or was recorded, and the row it landed on is not yet
            // what it will finally be.
            CreateTransactionDialogActions.chargeCardSuccess,
            CreateTransactionDialogActions.cardHeldSuccess,
            CreateTransactionDialogActions.captureHoldSuccess,
            CreateTransactionDialogActions.payLinkCreated,
            BlueSageRefundDialogActions.refundSuccess,
            // A push the device has finished with, whatever the outcome.
            BlueSageTerminalPushActions.pushStatusPolled,
            // The transaction-tracked poller, since the push mutations stopped
            // waiting for the device (2026-09-03). Settles the same way.
            BlueSageTerminalPushActions.pushStatusChecked,
            BlueSageTerminalPushActions.pushSuccess,
            BlueSageTerminalPushActions.completeSuccess,
        ),
        // The push actions fire repeatedly while the customer still has the
        // device; only the settled one is worth waiting on.
        // A BlueSage refund is followed by its LEG — `followBlueSageRefundLeg`
        // below — because it settles minutes later and this schedule stops at the
        // first change it sees, which would be the leg merely appearing.
        filter((action) => !('awaitsConfirmation' in action) || !action.awaitsConfirmation),
        filter((action) => !('push' in action) || action.push.settled),
        filter((action) => !('result' in action) || action.result.push.settled),
        filter((action) => !('status' in action) || action.status.push.settled),
        concatLatestFrom(() => store.select(selectTransactionJob)),
        filter(([ , job ]) => Boolean(job?.id)),
        // `switchMap`: a second money action supersedes the first wait rather
        // than running two schedules against the same job.
        switchMap(([ , job ]) => {
            const jobId = job.id;
            const before = transactionsFingerprint(job);

            return from(SETTLEMENT_POLL_DELAYS_MS).pipe(
                concatMap((delay) => timer(delay)),
                // ⚠ Stop the moment a read FAILS, rather than sending nine more.
                //
                // `JobsV3Page` is the heaviest query on the page. If it is failing
                // — a struggling backend, a dropped connection — then continuing
                // to schedule it makes the thing it is waiting on less likely to
                // recover, and turns one visible error into ten. The subscription
                // and a manual reload both remain.
                takeUntil(actions$.pipe(ofType(JobToolActions.backgroundRefreshError))),
                concatLatestFrom(() => store.select(selectTransactionJob)),
                // Inclusive, so the tick that FINDS the change still passes
                // through takeWhile — and is then dropped by the filter below,
                // because by then there is nothing left to ask for.
                takeWhile(([ , current ]) => transactionsFingerprint(current) === before, true),
                filter(([ , current ]) => transactionsFingerprint(current) === before),
                map(() => JobToolActions.backgroundRefreshTriggered({ jobId })),
            );
        }),
    );
}, { functional: true, dispatch: true });


/**
 * Backing off, in ms from the moment a refund or release was SENT. A terminal
 * void can legitimately run five and a half minutes, so this covers that and no
 * more; the job subscription is the fast path and usually gets there first.
 */
const REFUND_LEG_POLL_DELAYS_MS = [
    4_000, 6_000, 8_000, 12_000, 16_000, 20_000, 30_000, 30_000, 30_000, 30_000,
    30_000, 30_000, 30_000, 30_000, 30_000, 30_000, 30_000, 30_000, 30_000, 30_000, 30_000,
];

/**
 * Stop listening for an outcome after this long; the row still tells the story.
 *
 * Deliberately LONGER than `BLUESAGE_REFUND_LEG_STALE_AFTER_SECONDS`: the last
 * thing a follower does is re-read the job, and by then a leg still `pending`
 * is past the age at which the row stops treating it as in progress — so that
 * read is what unlocks Refund and Cancel pre-auth again without a page reload.
 */
const REFUND_LEG_OUTCOME_WINDOW_MS = 540_000;

/**
 * Follow a refund or hold release until its leg settles, and say what happened.
 *
 * Requirement: the mutation answers as soon as the leg exists and BlueSage
 * settles it in the background — a terminal refund can take minutes and the
 * production ingress cuts a request at 60 s (2026-09-17: every slow refund read
 * as a 504 while the backend carried on). So the OUTCOME is on the leg, later,
 * and possibly after the dialog was closed. This keeps the job re-read until
 * the leg leaves `pending`, then announces it once.
 *
 * `mergeMap`, not `switchMap`: two payments on one job can each have a refund
 * on a terminal, and following the second must not silence the first. Each
 * follower ends itself — on the outcome, on leaving the job, when a read fails,
 * or when the window closes.
 */
export const followBlueSageRefundLeg = createEffect((
    actions$ = inject(Actions),
    store = inject(Store),
) => {
    return actions$.pipe(
        ofType(
            BlueSageRefundDialogActions.refundSuccess,
            CancelPreauthDialogActions.cancelPreauthSuccess,
        ),
        // A payment taken elsewhere is only RECORDED as refunded: final at once.
        filter((action) => !('awaitsConfirmation' in action) || action.awaitsConfirmation),
        concatLatestFrom(() => store.select(selectTransactionJob)),
        filter(([ , job ]) => Boolean(job?.id)),
        mergeMap(([ action, job ]) => {
            const jobId = job.id;
            const releasing = action.type === CancelPreauthDialogActions.cancelPreauthSuccess.type;
            const { transactionId } = action;

            // Every stream below ends on the same three things, so they are named
            // once: the operator left the job, or the window closed, or it settled.
            const leftJob$ = store.select(selectTransactionJob).pipe(
                filter((current) => current?.id !== jobId),
                take(1),
            );
            const windowClosed$ = timer(REFUND_LEG_OUTCOME_WINDOW_MS);

            // Cold on purpose: each consumer reads the store for itself, so a leg
            // that had ALREADY settled when this began is seen by all of them.
            const settledLeg$ = store.select(selectTransactionJob).pipe(
                takeUntil(merge(leftJob$, windowClosed$)),
                map((current) => blueSageRefundLegOfAttempt(
                    current?.transactions, transactionId, action.knownLegIds,
                )),
                filter((leg) => Boolean(leg) && leg.stage !== TransactionStage.Pending),
                take(1),
            );

            const reads$ = from(REFUND_LEG_POLL_DELAYS_MS).pipe(
                concatMap((delay) => timer(delay)),
                takeUntil(merge(
                    settledLeg$,
                    leftJob$,
                    windowClosed$,
                    // Stop the moment a read FAILS — see `refreshJobWhileBlueSageSettles`.
                    actions$.pipe(ofType(JobToolActions.backgroundRefreshError)),
                )),
                map(() => JobToolActions.backgroundRefreshTriggered({ jobId })),
            );

            const outcome$ = settledLeg$.pipe(map((leg) => {
                if (leg.stage === TransactionStage.Paid) {
                    return releasing
                        ? BlueSageRefundLegActions.releaseConfirmed()
                        : BlueSageRefundLegActions.refundConfirmed();
                }

                const error = new Error(
                    describeBlueSageRefundFailure(leg) ?? 'BlueSage did not complete it — see the row on the job.',
                );
                return releasing
                    ? BlueSageRefundLegActions.releaseNotConfirmed({ error })
                    : BlueSageRefundLegActions.refundNotConfirmed({ error });
            }));

            // Requirement: never leave a dialog spinning for ever. If the window
            // closes with no outcome, say so; `rereadJobWhenRefundWindowCloses`
            // reacts to it.
            const gaveUp$ = windowClosed$.pipe(
                takeUntil(merge(settledLeg$, leftJob$)),
                map(() => BlueSageRefundLegActions.outcomeWindowClosed({ transactionId })),
            );

            return merge(reads$, outcome$, gaveUp$);
        }),
    );
}, { functional: true, dispatch: true });


/**
 * Read the job one last time when a refund's outcome window closes.
 *
 * Requirement: a row must not stay locked for ever. Refund, Cancel pre-auth and
 * Complete are withheld while a leg is young and `pending`; whether a leg is
 * still young depends on the clock, which no store change announces. The window
 * is longer than that age on purpose, so this read lands after it and the row
 * is looked at again without a page reload.
 */
export const rereadJobWhenRefundWindowCloses = createEffect((
    actions$ = inject(Actions),
    store = inject(Store),
) => {
    return actions$.pipe(
        ofType(BlueSageRefundLegActions.outcomeWindowClosed),
        concatLatestFrom(() => store.select(selectTransactionJob)),
        filter(([ , job ]) => Boolean(job?.id)),
        map(([ , job ]) => JobToolActions.backgroundRefreshTriggered({ jobId: job.id })),
    );
}, { functional: true, dispatch: true });


/**
 * Re-read the job the moment the server says a transaction changed.
 *
 * This is the FAST path, and the poll above is its backstop. The settling
 * webhook calls `transactions.update`, which publishes `jobChanged` with
 * `action: 'transaction updated'` — so the news of a payment settling already
 * reaches this browser within a second, and did throughout the testing where
 * rows appeared to hang for minutes. Nothing was listening.
 *
 * It re-reads rather than splicing the payload in. The event carries the raw
 * Neo4j transaction — no `job`, no `invoice`, no resolved `customer` — and
 * putting that shape into `job.transactions` would leave rows whose invoice tag
 * and payment-type name had silently vanished. A re-read costs one query and
 * cannot produce a half-populated row.
 */
/**
 * Settle rows the job still shows as pushes in flight.
 *
 * The status read is what finishes a push the middleware answered `pending`
 * and never confirmed by event — but only an open panel polls it. A dialog
 * closed on a live push, or a page left, leaves the row reading "in flight"
 * with Release and Complete withheld (live, 2026-09-03: a dropped push sat as
 * a pending deposit with only Delete on its menu). So when the job loads, each
 * such row is read once; the read asks the middleware and writes the answer.
 *
 * `switchMap`: navigating to another job abandons the previous job's reads.
 * The row the panel is actively following is left to the panel — two readers
 * settling one row would race to write it.
 */
export const settleInFlightBlueSagePushes = createEffect((
    actions$ = inject(Actions),
    store = inject(Store),
    statusGQL = inject(GetBlueSageTerminalPushStatusGQL),
) => {
    return actions$.pipe(
        ofType(JobToolActions.jobLoaded),
        filter(({ source }) => source === 'server'),
        concatLatestFrom(() => [
            store.select(blueSageTerminalPushFeature.selectTransactionId),
            store.select(blueSageTerminalPushFeature.selectPhase),
        ]),
        map(([ { job }, followedId, phase ]) => ({
            jobId: job.id,
            inFlight: (job.transactions ?? [])
                .filter((t) => !t.deletedAt
                    && t.stage === TransactionStage.Pending
                    && t.metadata?.bluesagePushOutcome === 'in_flight'
                    && !(phase === 'waiting' && t.id === followedId))
                .map((t) => t.id),
        })),
        filter(({ inFlight }) => inFlight.length > 0),
        // Each read fails on its own: `forkJoin` gives up on ALL of them the
        // moment one errors, which would throw away the rows that did settle.
        // A failed read becomes `{ error }` here and is reported once, while
        // the settled rows still trigger the re-read.
        switchMap(({ jobId, inFlight }) => forkJoin(inFlight.map((transactionId) =>
            statusGQL.fetch({ transactionId }, { fetchPolicy: 'no-cache' }).pipe(
                map((res) => ({ outcome: res.data.blueSageTerminalPushStatus.outcome, error: undefined as Error | undefined })),
                catchError((error: Error) => of({ outcome: 'in_flight', error })),
            ),
        )).pipe(
            mergeMap((results) => {
                const settled = results.filter((r) => r.outcome !== 'in_flight' && r.outcome !== 'none').length;
                const failed = results.find((r) => r.error);
                return from([
                    BlueSageTerminalPushActions.inFlightPushesSettled({ jobId, settled }),
                    ...(failed
                        ? [ BlueSageTerminalPushActions.inFlightPushesSettleError({ error: failed.error }) ]
                        : []),
                ]);
            }),
        )),
    );
}, { functional: true, dispatch: true });

/** Re-read the job once any of its in-flight rows has settled. */
export const refreshJobAfterInFlightPushesSettle = createEffect((
    actions$ = inject(Actions),
) => {
    return actions$.pipe(
        ofType(BlueSageTerminalPushActions.inFlightPushesSettled),
        filter(({ settled }) => settled > 0),
        map(({ jobId }) => JobToolActions.backgroundRefreshTriggered({ jobId })),
    );
}, { functional: true, dispatch: true });


export const refreshJobWhenTransactionChanges = createEffect((
    actions$ = inject(Actions),
    store = inject(Store),
) => {
    return actions$.pipe(
        ofType(...TRANSACTION_UPDATED_ACTIONS),
        // Coalesce a burst. Settling one payment can produce several updates in
        // quick succession, and each one would otherwise buy a full job read.
        debounceTime(TRANSACTION_UPDATE_DEBOUNCE_MS),
        concatLatestFrom(() => store.select(selectTransactionJob)),
        filter(([ , job ]) => Boolean(job?.id)),
        map(([ , job ]) => JobToolActions.backgroundRefreshTriggered({ jobId: job.id })),
    );
}, { functional: true, dispatch: true });
